import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useFacility } from '@/contexts/FacilityContext';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

const WARNING_BEFORE_MS = 2 * 60 * 1000; // 2 minutes warning
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll'];

export function SessionTimeoutGuard({ children }: { children: React.ReactNode }) {
    const { isAuthenticated, logout } = useAuth();
    const { settings } = useFacility();
    const navigate = useNavigate();
    const [showWarning, setShowWarning] = useState(false);

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimers = useCallback(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        if (warningRef.current) clearTimeout(warningRef.current);
    }, []);

    const doLogout = useCallback(async () => {
        setShowWarning(false);
        await logout();
        navigate('/login', { state: { timedOut: true } });
    }, [logout, navigate]);

    const resetTimers = useCallback(() => {
        if (!isAuthenticated) return;
        clearTimers();
        setShowWarning(false);

        const timeoutMs = settings.sessionTimeoutMinutes * 60 * 1000;

        warningRef.current = setTimeout(() => {
            setShowWarning(true);
        }, timeoutMs - WARNING_BEFORE_MS);

        timeoutRef.current = setTimeout(() => {
            void doLogout();
        }, timeoutMs);
    }, [isAuthenticated, settings.sessionTimeoutMinutes, clearTimers, doLogout]);

    useEffect(() => {
        if (!isAuthenticated) return;
        resetTimers();

        const handler = () => resetTimers();
        ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handler, { passive: true }));

        return () => {
            clearTimers();
            ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handler));
        };
    }, [isAuthenticated, resetTimers, clearTimers]);

    return (
        <>
            {children}
            <ConfirmDialog
                open={showWarning}
                title="Sitzung läuft ab"
                description={`Ihre Sitzung läuft in ${WARNING_BEFORE_MS / 60000} Minuten ab. Möchten Sie angemeldet bleiben?`}
                confirmLabel="Angemeldet bleiben"
                cancelLabel="Abmelden"
                onConfirm={resetTimers}
                onCancel={() => void doLogout()}
            />
        </>
    );
}
